import React, { useEffect, useState, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import { fetchUserByEmail, syncUser, fetchCards, fetchLeads, createLead, updateLead, deleteLead, fetchLeadAttachment, Lead, User, UserCreate, Card } from "../utils/api";
import Button from "./ui/Button";
import { useUserPersona } from "../hooks/useUserPersona";

const LEAD_STATUSES = [
  { key: 'all', label: 'All', color: '' },
  { key: 'new', label: 'New', color: 'bg-coffee-100 text-coffee-800 dark:text-cream-100' },
  { key: 'contacted', label: 'Contacted', color: 'bg-amber-50 text-amber-800 border border-amber-200' },
  { key: 'qualified', label: 'Qualified', color: 'bg-latte-100 text-latte-900' },
  { key: 'won', label: 'Won', color: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  { key: 'lost', label: 'Lost', color: 'bg-red-50 text-red-600 border border-red-200' },
];

// Persona-flavoured empty state: nudges the user toward the share action that
// matches the work they signed up to do.
const LeadsEmptyState: React.FC<{ hasAny: boolean }> = ({ hasAny }) => {
  const { persona } = useUserPersona();
  const tip = (() => {
    if (hasAny) return "No leads match your filter. Try a different status or clear the search.";
    switch (persona) {
      case "recruiter":
        return "No leads yet. Share your card link at the next career fair or hand out a QR with the booth poster.";
      case "sales":
        return "No leads yet. Drop your card link in your email signature and share it on the trade-show floor.";
      case "real-estate":
        return "No leads yet. Print the QR for your yard signs or open-house sign-in sheet.";
      case "consultant":
        return "No leads yet. Share your card after every discovery call — the link captures contact details automatically.";
      case "creator":
        return "No leads yet. Drop your card link in your bio and on every podcast outro.";
      default:
        return "No leads yet. Share your card and contacts will land here automatically.";
    }
  })();
  return (
    <div className="text-center py-16 px-4">
      <div className="w-12 h-12 rounded-xl bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 flex items-center justify-center mx-auto mb-4">
        <svg className="w-5 h-5 text-coffee-400 dark:text-cream-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
        </svg>
      </div>
      <div className="eyebrow mb-2">{hasAny ? 'No results' : 'No leads yet'}</div>
      <h3 className="font-semibold text-coffee-900 dark:text-cream-50 mb-1">{hasAny ? 'No leads match your filter.' : 'Nothing here yet.'}</h3>
      <p className="text-sm text-coffee-600 dark:text-cream-200 max-w-sm mx-auto">{tip}</p>
      {!hasAny && <Button to="/my-card" variant="primary" className="mt-4">Share my card</Button>}
    </div>
  );
};

const Leads: React.FC = () => {
  const { user } = useUser();
  const [dbUser, setDbUser] = useState<User | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [newLead, setNewLead] = useState({ firstName: '', lastName: '', email: '', company: '', jobTitle: '', phone: '', notes: '' });
  // D-005 attachment download: which lead is fetching, and any per-lead failure.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<{ id: string; message: string } | null>(null);

  // The attachment endpoint is owner-authenticated, so a plain <a href> would
  // arrive without the Clerk token and 401. Fetch it as a blob with the auth
  // header, then hand the browser an object URL to save.
  const handleDownloadAttachment = async (lead: Lead) => {
    setDownloadingId(lead.id);
    setDownloadError(null);
    let objectUrl: string | null = null;
    try {
      const blob = await fetchLeadAttachment(lead.id);
      objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = lead.attachmentFilename || 'attachment';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      setDownloadError({ id: lead.id, message: "Couldn't download that file. Try again?" });
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setDownloadingId(null);
    }
  };

  useEffect(() => { document.title = "Leads - WhoBrew"; }, []);

  const loadData = useCallback(async () => {
    if (!user) return;
    const email = user.primaryEmailAddress?.emailAddress;
    if (!email) return;
    setLoading(true);
    try {
      let fetchedUser: User;
      try { fetchedUser = await fetchUserByEmail(email); }
      catch { fetchedUser = await syncUser({ username: email.split("@")[0], email, firstName: user.firstName || "", lastName: user.lastName || "" } as UserCreate); }
      setDbUser(fetchedUser);
      const [userCards, userLeads] = await Promise.all([
        fetchCards(fetchedUser.id).catch(() => [] as Card[]),
        fetchLeads(fetchedUser.id).catch(() => [] as Lead[]),
      ]);
      setCards(userCards);
      setLeads(userLeads);
    } catch {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dbUser || cards.length === 0) return;
    try {
      const created = await createLead({
        cardId: cards[0].id,
        userId: dbUser.id,
        ...newLead,
        source: 'manual',
      });
      setLeads([created, ...leads]);
      setNewLead({ firstName: '', lastName: '', email: '', company: '', jobTitle: '', phone: '', notes: '' });
      setShowAddForm(false);
    } catch {}
  };

  const handleUpdateStatus = async (leadId: string, newStatus: string) => {
    try {
      const updated = await updateLead(leadId, { status: newStatus });
      setLeads(leads.map(l => l.id === leadId ? updated : l));
    } catch {}
  };

  const handleSaveNotes = async (leadId: string, notes: string) => {
    try {
      const updated = await updateLead(leadId, { notes });
      setLeads(leads.map(l => l.id === leadId ? updated : l));
      setEditingLead(null);
    } catch {}
  };

  const handleDelete = async (leadId: string) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await deleteLead(leadId);
      setLeads(leads.filter(l => l.id !== leadId));
    } catch {}
  };

  const handleExport = () => {
    const rows = filteredLeads.map(l => [
      l.email, l.firstName, l.lastName, l.company, l.jobTitle,
      l.status, l.emailPermission, '', l.source, new Date(l.createdAt).toLocaleDateString(),
    ]);
    const headers = ['Email address', 'First name', 'Last name', 'Company', 'Job title', 'Email status', 'Email permission status', 'Email Lists', 'Source Name', 'Created At'];
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(','))].join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `whobrew-leads-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLeads = leads
    .filter(l => filter === 'all' || l.status === filter)
    .filter(l => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (l.firstName + ' ' + l.lastName).toLowerCase().includes(s) || l.email.toLowerCase().includes(s) || l.company.toLowerCase().includes(s);
    });

  const counts = LEAD_STATUSES.reduce((acc, s) => {
    acc[s.key] = s.key === 'all' ? leads.length : leads.filter(l => l.status === s.key).length;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
        <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="animate-pulse space-y-2">
              <div className="h-3 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded w-16" />
              <div className="h-8 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl w-56" />
              <div className="h-4 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded w-48" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
      {/* Page header */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow mb-1.5">Leads</div>
              <h1 className="text-display text-3xl sm:text-4xl text-coffee-900 dark:text-cream-50">Your contacts.</h1>
              <p className="mt-1.5 text-coffee-600 dark:text-cream-200">
                <span className="font-mono text-coffee-900 dark:text-cream-50">{leads.length}</span> total — captured from your card and added manually
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 pt-1">
              <Button
                variant="secondary"
                onClick={handleExport}
                disabled={filteredLeads.length === 0}
              >
                Export CSV
              </Button>
              <Button variant="primary" onClick={() => setShowAddForm(true)}>Add lead</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter + Search bar */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="inline-flex gap-1 bg-white border border-coffee-100 dark:border-coffee-800 rounded-xl p-1 flex-wrap">
            {LEAD_STATUSES.map(s => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === s.key
                    ? 'bg-coffee-900 text-white'
                    : 'text-coffee-600 hover:text-coffee-900'
                }`}
              >
                {s.label} <span className="font-mono ml-0.5">({counts[s.key] || 0})</span>
              </button>
            ))}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or company..."
            className="flex-1 min-w-0 px-3 py-2 text-sm border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 bg-white text-coffee-900 dark:text-cream-50"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">

        {/* Add Lead Form */}
        {showAddForm && (
          <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 shadow-coffee">
            <div className="eyebrow mb-3">Add lead</div>
            <form onSubmit={handleAddLead} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={newLead.firstName} onChange={e => setNewLead({...newLead, firstName: e.target.value})} placeholder="First name" className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                <input value={newLead.lastName} onChange={e => setNewLead({...newLead, lastName: e.target.value})} placeholder="Last name" className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                <input type="email" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} placeholder="Email" className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                <input value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} placeholder="Phone" className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                <input value={newLead.company} onChange={e => setNewLead({...newLead, company: e.target.value})} placeholder="Company" className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                <input value={newLead.jobTitle} onChange={e => setNewLead({...newLead, jobTitle: e.target.value})} placeholder="Job title" className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
              </div>
              <textarea value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})} placeholder="Notes" rows={2} className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm resize-none bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
              <div className="flex gap-2 pt-2">
                <Button type="submit" variant="primary" size="sm">Save lead</Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </form>
          </div>
        )}

        {/* Leads List */}
        {filteredLeads.length === 0 ? (
          <LeadsEmptyState hasAny={leads.length > 0} />
        ) : (
          <div className="space-y-2">
            {filteredLeads.map(lead => {
              const statusDef = LEAD_STATUSES.find(s => s.key === lead.status) || LEAD_STATUSES[1];
              const isEditing = editingLead?.id === lead.id;
              const initials = [lead.firstName?.[0], lead.lastName?.[0]].filter(Boolean).join('').toUpperCase() || lead.email?.[0]?.toUpperCase() || '?';
              return (
                <div key={lead.id} className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-xl p-4 hover:bg-coffee-50/50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    {/* Avatar + info */}
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-full bg-coffee-100 dark:bg-coffee-800 border border-coffee-200 dark:border-coffee-700 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-coffee-700 dark:text-cream-200">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-coffee-900 dark:text-cream-50 text-sm">
                            {lead.firstName || lead.lastName ? `${lead.firstName} ${lead.lastName}`.trim() : lead.email || 'Unknown'}
                          </p>
                          {lead.source === 'card-form' && (
                            <span className="text-[10px] font-semibold tracking-eyebrow px-2 py-0.5 rounded-full bg-coffee-50 dark:bg-coffee-900 text-coffee-700 dark:text-cream-200 border border-coffee-100 dark:border-coffee-800">
                              via card
                            </span>
                          )}
                          {lead.source === 'contact-form' && (
                            <span className="text-[10px] font-semibold tracking-eyebrow px-2 py-0.5 rounded-full bg-accent-50 text-accent-700 border border-accent-100">
                              via form link
                            </span>
                          )}
                          {lead.source && lead.source !== 'card-form' && lead.source !== 'contact-form' && lead.source !== 'manual' && (
                            <span className="text-[10px] font-semibold tracking-eyebrow px-2 py-0.5 rounded-full bg-coffee-50 dark:bg-coffee-900 text-coffee-700 dark:text-cream-200 border border-coffee-100 dark:border-coffee-800">
                              {lead.source}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-coffee-500 dark:text-cream-300 mt-0.5">
                          {lead.email && <span>{lead.email}</span>}
                          {lead.company && <span>{lead.company}</span>}
                          {lead.jobTitle && <span>{lead.jobTitle}</span>}
                          {lead.phone && <span>{lead.phone}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Status chip + date */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="font-mono text-xs text-coffee-400 dark:text-cream-400 hidden sm:block">
                        {new Date(lead.createdAt).toLocaleDateString()}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusDef.color}`}>
                        {statusDef.label}
                      </span>
                    </div>
                  </div>

                  {/* Notes */}
                  {isEditing ? (
                    <div className="mt-3">
                      <textarea
                        value={editingLead.notes}
                        onChange={e => setEditingLead({...editingLead, notes: e.target.value})}
                        rows={3}
                        className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm resize-none bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="primary" onClick={() => handleSaveNotes(lead.id, editingLead.notes)}>Save</Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditingLead(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : lead.notes ? (
                    <p
                      className="text-sm text-coffee-600 dark:text-cream-200 mt-2 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-lg px-3 py-2 cursor-pointer hover:bg-coffee-100 transition-colors"
                      onClick={() => setEditingLead(lead)}
                    >
                      {lead.notes}
                    </p>
                  ) : (
                    <button
                      onClick={() => setEditingLead({...lead, notes: ''})}
                      className="text-xs text-coffee-400 dark:text-cream-400 hover:text-coffee-600 mt-2 transition-colors"
                    >
                      + Add notes
                    </button>
                  )}

                  {/* Form answers — present only when this lead came from a
                      contact-form link and the visitor answered custom fields. */}
                  {lead.extraFields && Object.keys(lead.extraFields).length > 0 && (
                    <div className="mt-3 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] font-mono uppercase tracking-eyebrow text-coffee-500 dark:text-cream-300 mb-2">
                        Form answers
                      </p>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {Object.entries(lead.extraFields).map(([label, value]) => (
                          <div key={label} className="min-w-0">
                            <dt className="text-xs text-coffee-500 dark:text-cream-300 truncate">{label}</dt>
                            <dd className="text-coffee-900 dark:text-cream-50 break-words">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}

                  {/* Visitor-submitted attachment (D-005). Only the owner can
                      fetch the bytes; the lead payload carries just its name. */}
                  {lead.hasAttachment && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => handleDownloadAttachment(lead)}
                        disabled={downloadingId === lead.id}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-coffee-700 dark:text-cream-100 border border-coffee-200 dark:border-coffee-700 rounded-lg px-2.5 py-1.5 hover:bg-coffee-50 dark:hover:bg-coffee-900 transition-colors disabled:opacity-50"
                      >
                        {downloadingId === lead.id
                          ? 'Downloading…'
                          : `Download ${lead.attachmentFilename || 'attachment'}`}
                      </button>
                      {downloadError?.id === lead.id && (
                        <p role="alert" className="mt-1 text-xs text-red-600">{downloadError.message}</p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-coffee-50">
                    <div className="flex gap-1 flex-wrap">
                      {LEAD_STATUSES.filter(s => s.key !== 'all' && s.key !== lead.status).map(s => (
                        <button
                          key={s.key}
                          onClick={() => handleUpdateStatus(lead.id, s.key)}
                          className={`px-2 py-1 rounded-lg text-xs font-semibold transition-colors hover:opacity-90 ${s.color}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => handleDelete(lead.id)}
                      className="text-xs text-red-400 hover:text-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leads;
